import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concat, from, Observable, of } from "rxjs";
import {
  catchError,
  concatMap,
  filter,
  map,
  mergeMap,
  switchMap,
  tap,
} from "rxjs/operators";
import {
  ForumAttachment,
  ForumAttachmentConfig,
  ForumMember,
  ForumOverviewGroup,
  ForumPost,
  ForumPostReaction,
  ForumReactionOption,
  ForumThreadRead,
  ForumThread,
  ForumThreadReactionDetails,
} from "src/app/shared/interfaces/forum";
import { PaginationData } from "src/app/shared/interfaces/orgadmin/course-organization";
import { ForumService } from "src/app/shared/services/forum/forum.service";
import { ForumPendingAttachmentsService } from "src/app/shared/services/forum/forum-pending-attachments.service";
import { S3ApiService } from "src/app/shared/services/s3/s3-api.service";
import { DialogWidth } from "src/app/shared/enums/modal-width";
import {
  ForumCreateThreadModalComponent,
  ForumCreateThreadModalResult,
} from "src/app/modules/forum/components/forum-create-thread-modal/forum-create-thread-modal.component";
import { ForumThreadReadUsersModalComponent } from "src/app/modules/forum/components/forum-thread-read-users-modal/forum-thread-read-users-modal.component";
import {
  ForumThreadReactionUsersModalComponent,
  ForumThreadReactionUsersModalResult,
} from "src/app/modules/forum/components/forum-thread-reaction-users-modal/forum-thread-reaction-users-modal.component";
import { extractMentionedUserIds } from "src/app/modules/forum/utils/forum-mentions.util";
import {
  forumAttachmentConfigLoad,
  forumAttachmentConfigLoadFailure,
  forumAttachmentConfigLoadSuccess,
  forumAttachmentCreate,
  forumAttachmentCreateFailure,
  forumAttachmentCreateSuccess,
  forumAttachmentDelete,
  forumAttachmentDeleteFailure,
  forumAttachmentDeleteSuccess,
  forumAttachmentsLoad,
  forumAttachmentsLoadFailure,
  forumAttachmentsLoadSuccess,
  forumPostCreate,
  forumPostAttachmentsUploadFinished,
  forumPostCreateFailure,
  forumPostCreateSuccess,
  forumPostDelete,
  forumPostDeleteFailure,
  forumPostDeleteSuccess,
  forumPostReactionCreate,
  forumPostReactionCreateFailure,
  forumPostReactionCreateSuccess,
  forumPostReactionDelete,
  forumPostReactionDeleteFailure,
  forumPostReactionDeleteSuccess,
  forumPostReactionsLoad,
  forumPostReactionsLoadFailure,
  forumPostReactionsLoadSuccess,
  forumMembersLoad,
  forumMembersLoadFailure,
  forumMembersLoadSuccess,
  forumOverviewGroupsLoad,
  forumOverviewGroupsLoadFailure,
  forumOverviewGroupsLoadSuccess,
  forumPostsLoad,
  forumPostsLoadFailure,
  forumPostsLoadSuccess,
  forumPostUpdate,
  forumPostUpdateFailure,
  forumPostUpdateSuccess,
  forumThreadCreate,
  forumThreadCreateFailure,
  forumThreadCreateSuccess,
  forumThreadDelete,
  forumThreadDeleteFailure,
  forumThreadDeleteSuccess,
  forumThreadDetailsLoad,
  forumThreadDetailsLoadFailure,
  forumThreadDetailsLoadSuccess,
  forumThreadsLoad,
  forumThreadsLoadFailure,
  forumThreadsLoadSuccess,
  forumThreadUpdate,
  forumThreadUpdateFailure,
  forumThreadUpdateSuccess,
  forumThreadAttachmentCreate,
  forumThreadAttachmentCreateFailure,
  forumThreadAttachmentCreateSuccess,
  forumThreadAttachmentDelete,
  forumThreadAttachmentDeleteFailure,
  forumThreadAttachmentDeleteSuccess,
  forumThreadAttachmentsLoad,
  forumThreadAttachmentsLoadFailure,
  forumThreadAttachmentsLoadSuccess,
  forumThreadFavorite,
  forumThreadFavoriteFailure,
  forumThreadFavoriteSuccess,
  forumThreadReactionCreate,
  forumThreadReactionCreateFailure,
  forumThreadReactionCreateSuccess,
  forumThreadReactionDelete,
  forumThreadReactionDeleteFailure,
  forumThreadReactionDeleteSuccess,
  forumThreadReactionDetailsLoad,
  forumThreadReactionDetailsLoadFailure,
  forumThreadReactionDetailsLoadSuccess,
  forumThreadReadsLoad,
  forumThreadReadsLoadFailure,
  forumThreadReadsLoadSuccess,
  forumThreadPin,
  forumThreadPinFailure,
  forumThreadPinSuccess,
  forumThreadRestore,
  forumThreadRestoreFailure,
  forumThreadRestoreSuccess,
  forumThreadUnpin,
  forumThreadUnpinFailure,
  forumThreadUnpinSuccess,
  forumThreadUnfavorite,
  forumThreadUnfavoriteFailure,
  forumThreadUnfavoriteSuccess,
  forumThreadsLockAll,
  forumThreadsLockAllFailure,
  forumThreadsLockAllSuccess,
  forumReactionOptionsLoad,
  forumReactionOptionsLoadFailure,
  forumReactionOptionsLoadSuccess,
  forumThreadsUnlockAll,
  forumThreadsUnlockAllFailure,
  forumThreadsUnlockAllSuccess,
  forumThreadDeletePernament,
  forumThreadDeletePernamentSuccess,
  forumThreadDeletePernamentFailure,
} from "../actions/forum.actions";
import { createNotification } from "src/app/shared/utils/create-notification";
import { NotificationsService } from "src/app/shared/services/notifications/notifications.service";
import { TranslateService } from "@ngx-translate/core";

@Injectable()
export class ForumEffect {
  loadAttachmentConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumAttachmentConfigLoad),
      switchMap((param) =>
        this.forumService
          .getAttachmentConfig(param.courseId, param.groupId)
          .pipe(
            map((config: ForumAttachmentConfig) =>
              forumAttachmentConfigLoadSuccess({ config }),
            ),
            catchError(({ error }) =>
              of(forumAttachmentConfigLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  loadThreads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadsLoad),
      switchMap((param) =>
        this.forumService
          .getThreads(
            param.courseId,
            param.groupId,
            param.search,
            param.archived,
            param.page,
          )
          .pipe(
            map((res: PaginationData<ForumThread>) =>
              forumThreadsLoadSuccess({
                threads: res.results || [],
                next: res.next ?? null,
                page: param.page ?? 1,
              }),
            ),
            catchError(({ error }) => of(forumThreadsLoadFailure({ error }))),
          ),
      ),
    ),
  );

  loadMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumMembersLoad),
      switchMap((param) =>
        this.forumService.getMembers(param.courseId, param.groupId).pipe(
          map((members: ForumMember[]) => forumMembersLoadSuccess({ members })),
          catchError(({ error }) => of(forumMembersLoadFailure({ error }))),
        ),
      ),
    ),
  );

  loadOverviewGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumOverviewGroupsLoad),
      switchMap((param) =>
        this.forumService
          .getOverviewGroups(
            param.page ?? 1,
            param.groupName,
            param.courseName,
            param.ordering,
          )
          .pipe(
            map((res: PaginationData<ForumOverviewGroup>) =>
              forumOverviewGroupsLoadSuccess({
                groups: res.results || [],
                next: res.next ?? null,
                page: param.page ?? 1,
              }),
            ),
            catchError(({ error }) =>
              of(forumOverviewGroupsLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  loadThreadDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadDetailsLoad),
      switchMap((param) =>
        this.forumService
          .getThreadDetails(param.courseId, param.groupId, param.threadId)
          .pipe(
            map((thread: ForumThread) =>
              param.openEditModal
                ? forumThreadDetailsLoadSuccess({
                    thread,
                    editModalContext: {
                      courseId: param.courseId,
                      groupId: param.groupId,
                      members: param.members,
                      attachmentConfig: param.attachmentConfig,
                    },
                  })
                : forumThreadDetailsLoadSuccess({ thread }),
            ),
            catchError(({ error }) =>
              of(forumThreadDetailsLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  createThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadCreate),
      switchMap((param) =>
        this.forumService
          .createThread(param.courseId, param.groupId, param.payload)
          .pipe(
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GROUPS.LIST.FORUM_THREAD_CREATED_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
            switchMap((thread: ForumThread) =>
              concat(
                of(forumThreadCreateSuccess({ thread })),
                this.uploadThreadAttachments(
                  param.courseId,
                  param.groupId,
                  thread.id,
                  param.requestId,
                ),
              ),
            ),
            catchError(({ error }) => of(forumThreadCreateFailure({ error }))),
          ),
      ),
    ),
  );

  updateThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadUpdate),
      switchMap((param) =>
        this.forumService
          .updateThread(
            param.courseId,
            param.groupId,
            param.threadId,
            param.payload,
          )
          .pipe(
            switchMap((thread: ForumThread) =>
              concat(
                of(forumThreadUpdateSuccess({ thread })),
                this.deleteThreadAttachments(
                  param.courseId,
                  param.groupId,
                  param.threadId,
                  param.removedAttachmentIds,
                ),
                this.uploadThreadAttachments(
                  param.courseId,
                  param.groupId,
                  param.threadId,
                  param.requestId,
                ),
              ),
            ),
            tap(() => {
              if (typeof param.payload.is_locked !== "boolean") {
                return;
              }

              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      param.payload.is_locked
                        ? "GROUPS.LIST.FORUM_THREAD_LOCK_NOTIFICATION"
                        : "GROUPS.LIST.FORUM_THREAD_UNLOCK_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
            catchError(({ error }) => of(forumThreadUpdateFailure({ error }))),
          ),
      ),
    ),
  );

  deleteThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadDelete),
      switchMap((param) =>
        this.forumService
          .deleteThread(param.courseId, param.groupId, param.threadId)
          .pipe(
            map(() => forumThreadDeleteSuccess({ threadId: param.threadId })),
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GROUPS.LIST.FORUM_THREAD_HIDE_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
            catchError(({ error }) => of(forumThreadDeleteFailure({ error }))),
          ),
      ),
    ),
  );

  deletePernamentThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadDeletePernament),
      switchMap((param) =>
        this.forumService
          .deletePernamentThread(param.courseId, param.groupId, param.threadId)
          .pipe(
            map(() =>
              forumThreadDeletePernamentSuccess({ threadId: param.threadId }),
            ),
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GROUPS.LIST.FORUM_THREAD_DELETE_PERNAMENT_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
            catchError(({ error }) =>
              of(forumThreadDeletePernamentFailure({ error })),
            ),
          ),
      ),
    ),
  );

  restoreThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadRestore),
      switchMap((param) =>
        this.forumService
          .restoreThread(param.courseId, param.groupId, param.threadId)
          .pipe(
            map((thread: ForumThread) => forumThreadRestoreSuccess({ thread })),
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GROUPS.LIST.FORUM_THREAD_RESTORE_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
            catchError(({ error }) => of(forumThreadRestoreFailure({ error }))),
          ),
      ),
    ),
  );

  pinThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadPin),
      switchMap((param) =>
        this.forumService
          .pinThread(param.courseId, param.groupId, param.threadId)
          .pipe(
            map((thread: ForumThread) => forumThreadPinSuccess({ thread })),
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GROUPS.LIST.FORUM_THREAD_PIN_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
            catchError(({ error }) => of(forumThreadPinFailure({ error }))),
          ),
      ),
    ),
  );

  unpinThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadUnpin),
      switchMap((param) =>
        this.forumService
          .unpinThread(param.courseId, param.groupId, param.threadId)
          .pipe(
            map(() => forumThreadUnpinSuccess({ threadId: param.threadId })),
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GROUPS.LIST.FORUM_THREAD_UNPIN_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
            catchError(({ error }) => of(forumThreadUnpinFailure({ error }))),
          ),
      ),
    ),
  );

  loadPosts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumPostsLoad),
      switchMap((param) =>
        this.forumService
          .getThreadPosts(
            param.courseId,
            param.groupId,
            param.threadId,
            param.ordering,
            param.page,
          )
          .pipe(
            map((res: PaginationData<ForumPost>) =>
              forumPostsLoadSuccess({
                threadId: param.threadId,
                posts: res.results || [],
                next: res.next ?? null,
                page: param.page ?? 1,
              }),
            ),
            catchError(({ error }) => of(forumPostsLoadFailure({ error }))),
          ),
      ),
    ),
  );

  createPost$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumPostCreate),
      switchMap((param) =>
        this.forumService
          .createPost(
            param.courseId,
            param.groupId,
            param.threadId,
            param.payload,
          )
          .pipe(
            switchMap((post: ForumPost) => {
              const pendingAttachmentsCount = param.requestId
                ? this.pendingAttachmentsService.peek(param.requestId).length
                : 0;

              return concat(
                of(
                  forumPostCreateSuccess({
                    threadId: param.threadId,
                    post,
                    pendingAttachmentsCount,
                  }),
                ),
                this.uploadPostAttachments(
                  param.courseId,
                  param.groupId,
                  param.threadId,
                  post.id,
                  param.requestId,
                ),
                of(
                  forumPostAttachmentsUploadFinished({
                    threadId: param.threadId,
                    postId: post.id,
                  }),
                ),
              );
            }),
            catchError(({ error }) => of(forumPostCreateFailure({ error }))),
          ),
      ),
    ),
  );

  updatePost$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumPostUpdate),
      switchMap((param) =>
        this.forumService
          .updatePost(
            param.courseId,
            param.groupId,
            param.threadId,
            param.postId,
            param.payload,
          )
          .pipe(
            switchMap((post: ForumPost) =>
              concat(
                of(forumPostUpdateSuccess({ threadId: param.threadId, post })),
                this.deletePostAttachments(
                  param.courseId,
                  param.groupId,
                  param.threadId,
                  param.postId,
                  param.removedAttachmentIds,
                ),
                this.uploadPostAttachments(
                  param.courseId,
                  param.groupId,
                  param.threadId,
                  param.postId,
                  param.requestId,
                ),
              ),
            ),
            catchError(({ error }) => of(forumPostUpdateFailure({ error }))),
          ),
      ),
    ),
  );

  deletePost$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumPostDelete),
      switchMap((param) =>
        this.forumService
          .deletePost(
            param.courseId,
            param.groupId,
            param.threadId,
            param.postId,
          )
          .pipe(
            map(() =>
              forumPostDeleteSuccess({
                threadId: param.threadId,
                postId: param.postId,
              }),
            ),
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GROUPS.LIST.FORUM_POST_DELETE_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
            catchError(({ error }) => of(forumPostDeleteFailure({ error }))),
          ),
      ),
    ),
  );

  loadAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumAttachmentsLoad),
      mergeMap((param) =>
        this.forumService
          .getPostAttachments(
            param.courseId,
            param.groupId,
            param.threadId,
            param.postId,
          )
          .pipe(
            map((res: PaginationData<ForumAttachment>) =>
              forumAttachmentsLoadSuccess({
                postId: param.postId,
                attachments: res.results || [],
              }),
            ),
            catchError(({ error }) =>
              of(forumAttachmentsLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  createAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumAttachmentCreate),
      switchMap((param) =>
        this.forumService
          .createPostAttachment(
            param.courseId,
            param.groupId,
            param.threadId,
            param.postId,
            param.payload,
          )
          .pipe(
            map((attachment: ForumAttachment) =>
              forumAttachmentCreateSuccess({
                postId: param.postId,
                attachment,
              }),
            ),
            catchError(({ error }) =>
              of(forumAttachmentCreateFailure({ error })),
            ),
          ),
      ),
    ),
  );

  deleteAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumAttachmentDelete),
      switchMap((param) =>
        this.forumService
          .deletePostAttachment(
            param.courseId,
            param.groupId,
            param.threadId,
            param.postId,
            param.attachmentId,
          )
          .pipe(
            map(() =>
              forumAttachmentDeleteSuccess({
                postId: param.postId,
                attachmentId: param.attachmentId,
              }),
            ),
            catchError(({ error }) =>
              of(forumAttachmentDeleteFailure({ error })),
            ),
          ),
      ),
    ),
  );

  loadThreadAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadAttachmentsLoad),
      mergeMap((param) =>
        this.forumService
          .getThreadAttachments(param.courseId, param.groupId, param.threadId)
          .pipe(
            map((res: PaginationData<ForumAttachment>) =>
              forumThreadAttachmentsLoadSuccess({
                threadId: param.threadId,
                attachments: res.results || [],
              }),
            ),
            catchError(({ error }) =>
              of(forumThreadAttachmentsLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  createThreadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadAttachmentCreate),
      switchMap((param) =>
        this.forumService
          .createThreadAttachment(
            param.courseId,
            param.groupId,
            param.threadId,
            param.payload,
          )
          .pipe(
            map((attachment: ForumAttachment) =>
              forumThreadAttachmentCreateSuccess({
                threadId: param.threadId,
                attachment,
              }),
            ),
            catchError(({ error }) =>
              of(forumThreadAttachmentCreateFailure({ error })),
            ),
          ),
      ),
    ),
  );

  deleteThreadAttachment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadAttachmentDelete),
      switchMap((param) =>
        this.forumService
          .deleteThreadAttachment(
            param.courseId,
            param.groupId,
            param.threadId,
            param.attachmentId,
          )
          .pipe(
            map(() =>
              forumThreadAttachmentDeleteSuccess({
                threadId: param.threadId,
                attachmentId: param.attachmentId,
              }),
            ),
            catchError(({ error }) =>
              of(forumThreadAttachmentDeleteFailure({ error })),
            ),
          ),
      ),
    ),
  );

  openThreadEditModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadDetailsLoadSuccess),
      filter(({ editModalContext }) => Boolean(editModalContext)),
      mergeMap(({ thread, editModalContext }) =>
        this.dialog
          .open(ForumCreateThreadModalComponent, {
            autoFocus: false,
            width: DialogWidth.Large,
            maxWidth: "95vw",
            maxHeight: "90dvh",
            data: {
              mode: "edit",
              thread,
              members: editModalContext?.members,
              attachments: thread.attachments || [],
              attachmentConfig: editModalContext?.attachmentConfig,
            },
          })
          .afterClosed()
          .pipe(
            filter((result): result is ForumCreateThreadModalResult =>
              Boolean(result),
            ),
            map((result) => {
              const requestId =
                result.attachments && result.attachments.length
                  ? crypto.randomUUID()
                  : undefined;

              if (requestId && result.attachments?.length) {
                this.pendingAttachmentsService.set(
                  requestId,
                  result.attachments,
                );
              }

              return forumThreadUpdate({
                courseId: editModalContext!.courseId,
                groupId: editModalContext!.groupId,
                threadId: thread.id,
                payload: {
                  title: result.title,
                  content: result.content,
                  mentioned_user_ids: extractMentionedUserIds(result.content),
                },
                requestId,
                removedAttachmentIds: result.removedAttachmentIds || [],
              });
            }),
            tap(() => {
              this.notificationsService.showToastr(
                createNotification({
                  content: {
                    message: this.translate.instant(
                      "GROUPS.LIST.FORUM_THREAD_EDITED_NOTIFICATION",
                    ),
                  },
                }),
              );
            }),
          ),
      ),
    ),
  );

  loadThreadReads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadReadsLoad),
      switchMap((param) =>
        this.forumService
          .getThreadReads(param.courseId, param.groupId, param.threadId)
          .pipe(
            map((reads: ForumThreadRead[]) =>
              forumThreadReadsLoadSuccess({
                threadId: param.threadId,
                reads,
                openModal: param.openModal,
              }),
            ),
            catchError(({ error }) =>
              of(forumThreadReadsLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  openThreadReadsModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(forumThreadReadsLoadSuccess),
        filter(({ openModal }) => Boolean(openModal)),
        tap(({ reads }) => {
          this.dialog.open(ForumThreadReadUsersModalComponent, {
            width: DialogWidth.Medium,
            autoFocus: false,
            data: {
              reads,
            },
          });
        }),
      ),
    { dispatch: false },
  );

  favoriteThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadFavorite),
      switchMap((param) =>
        this.forumService
          .favoriteThread(param.courseId, param.groupId, param.threadId)
          .pipe(
            map(() => forumThreadFavoriteSuccess({ threadId: param.threadId })),
            catchError(({ error }) =>
              of(forumThreadFavoriteFailure({ error })),
            ),
          ),
      ),
    ),
  );

  unfavoriteThread$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadUnfavorite),
      switchMap((param) =>
        this.forumService
          .unfavoriteThread(param.courseId, param.groupId, param.threadId)
          .pipe(
            map(() =>
              forumThreadUnfavoriteSuccess({ threadId: param.threadId }),
            ),
            catchError(({ error }) =>
              of(forumThreadUnfavoriteFailure({ error })),
            ),
          ),
      ),
    ),
  );

  lockAllThreads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadsLockAll),
      switchMap((param) =>
        this.forumService
          .lockAllThreads(param.courseId, param.groupId, param.threadId)
          .pipe(
            map(() => forumThreadsLockAllSuccess()),
            catchError(({ error }) =>
              of(forumThreadsLockAllFailure({ error })),
            ),
          ),
      ),
    ),
  );

  unlockAllThreads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadsUnlockAll),
      switchMap((param) =>
        this.forumService
          .unlockAllThreads(param.courseId, param.groupId, param.threadId)
          .pipe(
            map(() => forumThreadsUnlockAllSuccess()),
            catchError(({ error }) =>
              of(forumThreadsUnlockAllFailure({ error })),
            ),
          ),
      ),
    ),
  );

  loadReactionOptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumReactionOptionsLoad),
      switchMap((param) =>
        this.forumService
          .getReactionOptions(param.courseId, param.groupId)
          .pipe(
            map((options: ForumReactionOption[]) =>
              forumReactionOptionsLoadSuccess({ options }),
            ),
            catchError(({ error }) =>
              of(forumReactionOptionsLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  createThreadReaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadReactionCreate),
      switchMap((param) =>
        this.forumService
          .createThreadReaction(
            param.courseId,
            param.groupId,
            param.threadId,
            param.payload,
          )
          .pipe(
            map((reaction) =>
              forumThreadReactionCreateSuccess({
                courseId: param.courseId,
                groupId: param.groupId,
                threadId: param.threadId,
                reaction,
              }),
            ),
            catchError(({ error }) =>
              of(forumThreadReactionCreateFailure({ error })),
            ),
          ),
      ),
    ),
  );

  deleteThreadReaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadReactionDelete),
      switchMap((param) =>
        this.forumService
          .deleteThreadReaction(
            param.courseId,
            param.groupId,
            param.threadId,
            param.emoji,
          )
          .pipe(
            map(() =>
              forumThreadReactionDeleteSuccess({
                courseId: param.courseId,
                groupId: param.groupId,
                threadId: param.threadId,
                emoji: param.emoji,
              }),
            ),
            catchError(({ error }) =>
              of(forumThreadReactionDeleteFailure({ error })),
            ),
          ),
      ),
    ),
  );

  loadThreadReactionDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadReactionDetailsLoad),
      switchMap((param) =>
        this.forumService
          .getThreadReactionDetails(
            param.courseId,
            param.groupId,
            param.threadId,
            param.emoji,
          )
          .pipe(
            map((details: ForumThreadReactionDetails) =>
              forumThreadReactionDetailsLoadSuccess({
                courseId: param.courseId,
                groupId: param.groupId,
                threadId: param.threadId,
                emoji: param.emoji,
                currentUserId: param.currentUserId,
                details,
                openModal: param.openModal,
              }),
            ),
            catchError(({ error }) =>
              of(forumThreadReactionDetailsLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  openThreadReactionDetailsModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumThreadReactionDetailsLoadSuccess),
      filter(({ openModal }) => Boolean(openModal)),
      mergeMap(
        ({ courseId, groupId, threadId, emoji, currentUserId, details }) =>
          this.dialog
            .open(ForumThreadReactionUsersModalComponent, {
              width: DialogWidth.Medium,
              autoFocus: false,
              data: { ...details, currentUserId },
            })
            .afterClosed()
            .pipe(
              filter(
                (
                  result: ForumThreadReactionUsersModalResult | undefined,
                ): result is ForumThreadReactionUsersModalResult =>
                  result?.action === "delete",
              ),
              map(() =>
                forumThreadReactionDelete({
                  courseId,
                  groupId,
                  threadId,
                  emoji,
                }),
              ),
            ),
      ),
    ),
  );

  createPostReaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumPostReactionCreate),
      switchMap((param) =>
        this.forumService
          .createPostReaction(
            param.courseId,
            param.groupId,
            param.threadId,
            param.postId,
            param.payload,
          )
          .pipe(
            map(() =>
              forumPostReactionCreateSuccess({
                courseId: param.courseId,
                groupId: param.groupId,
                threadId: param.threadId,
                postId: param.postId,
                emoji: param.payload.emoji,
              }),
            ),
            catchError(({ error }) =>
              of(forumPostReactionCreateFailure({ error })),
            ),
          ),
      ),
    ),
  );

  deletePostReaction$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumPostReactionDelete),
      switchMap((param) =>
        this.forumService
          .deletePostReaction(
            param.courseId,
            param.groupId,
            param.threadId,
            param.postId,
            param.emoji,
          )
          .pipe(
            map(() =>
              forumPostReactionDeleteSuccess({
                courseId: param.courseId,
                groupId: param.groupId,
                threadId: param.threadId,
                postId: param.postId,
                emoji: param.emoji,
              }),
            ),
            catchError(({ error }) =>
              of(forumPostReactionDeleteFailure({ error })),
            ),
          ),
      ),
    ),
  );

  loadPostReactions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(forumPostReactionsLoad),
      mergeMap((param) =>
        this.forumService
          .getPostReactions(
            param.courseId,
            param.groupId,
            param.threadId,
            param.postId,
          )
          .pipe(
            map((reactions: ForumPostReaction[]) =>
              forumPostReactionsLoadSuccess({
                postId: param.postId,
                reactions,
              }),
            ),
            catchError(({ error }) =>
              of(forumPostReactionsLoadFailure({ error })),
            ),
          ),
      ),
    ),
  );

  private uploadThreadAttachments(
    courseId: string,
    groupId: string,
    threadId: string,
    requestId?: string,
  ): Observable<ReturnType<typeof forumThreadAttachmentCreateSuccess>> {
    const files = requestId
      ? this.pendingAttachmentsService.consume(requestId)
      : [];

    if (!files.length) {
      return from([]);
    }

    return from(files).pipe(
      concatMap((file) =>
        this.uploadAttachmentToS3(courseId, groupId, file).pipe(
          switchMap((payload) =>
            this.forumService
              .createThreadAttachment(courseId, groupId, threadId, payload)
              .pipe(
                map((attachment) =>
                  forumThreadAttachmentCreateSuccess({
                    threadId,
                    attachment,
                  }),
                ),
              ),
          ),
          catchError(() => from([])),
        ),
      ),
    );
  }

  private deleteThreadAttachments(
    courseId: string,
    groupId: string,
    threadId: string,
    attachmentIds?: string[],
  ): Observable<ReturnType<typeof forumThreadAttachmentDeleteSuccess>> {
    if (!attachmentIds?.length) {
      return from([]);
    }

    return from(attachmentIds).pipe(
      concatMap((attachmentId) =>
        this.forumService
          .deleteThreadAttachment(courseId, groupId, threadId, attachmentId)
          .pipe(
            map(() =>
              forumThreadAttachmentDeleteSuccess({
                threadId,
                attachmentId,
              }),
            ),
            catchError(() => from([])),
          ),
      ),
    );
  }

  private uploadPostAttachments(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
    requestId?: string,
  ): Observable<ReturnType<typeof forumAttachmentCreateSuccess>> {
    const files = requestId
      ? this.pendingAttachmentsService.consume(requestId)
      : [];

    if (!files.length) {
      return from([]);
    }

    return from(files).pipe(
      concatMap((file) =>
        this.uploadAttachmentToS3(courseId, groupId, file).pipe(
          switchMap((payload) =>
            this.forumService
              .createPostAttachment(
                courseId,
                groupId,
                threadId,
                postId,
                payload,
              )
              .pipe(
                map((attachment) =>
                  forumAttachmentCreateSuccess({
                    postId,
                    attachment,
                  }),
                ),
              ),
          ),
          catchError(() => from([])),
        ),
      ),
    );
  }

  private deletePostAttachments(
    courseId: string,
    groupId: string,
    threadId: string,
    postId: string,
    attachmentIds?: string[],
  ): Observable<ReturnType<typeof forumAttachmentDeleteSuccess>> {
    if (!attachmentIds?.length) {
      return from([]);
    }

    return from(attachmentIds).pipe(
      concatMap((attachmentId) =>
        this.forumService
          .deletePostAttachment(
            courseId,
            groupId,
            threadId,
            postId,
            attachmentId,
          )
          .pipe(
            map(() =>
              forumAttachmentDeleteSuccess({
                postId,
                attachmentId,
              }),
            ),
            catchError(() => from([])),
          ),
      ),
    );
  }

  private uploadAttachmentToS3(
    courseId: string,
    groupId: string,
    file: File,
  ): Observable<{
    filename: string;
    size: number;
    content_type: string;
    s3_key: string;
  }> {
    return this.forumService
      .getAttachmentS3Url(courseId, groupId, file.name)
      .pipe(
        switchMap((response) => {
          const formData = new FormData();

          Object.entries(response.s3_url.fields).forEach(
            ([fieldName, fieldValue]) => {
              formData.append(fieldName, fieldValue.toString());
            },
          );

          formData.append("file", file, file.name);

          return this.s3Api.upload(response.s3_url.url, formData).pipe(
            map(() => ({
              filename: file.name,
              size: file.size,
              content_type: file.type || "application/octet-stream",
              s3_key: response.s3_key,
            })),
          );
        }),
      );
  }

  constructor(
    private actions$: Actions,
    private forumService: ForumService,
    private pendingAttachmentsService: ForumPendingAttachmentsService,
    private s3Api: S3ApiService,
    private dialog: MatDialog,
    private notificationsService: NotificationsService,
    private translate: TranslateService,
  ) {}
}
